import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Box,
  CircularProgress
} from '@material-ui/core';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import { TreasureService } from '../services/TreasureService';

// Component cài đặt hệ thống mưa quà cơ bản
function RainSystemSettings({ settings, onSettingsChange, onSave, loading, onCancelChanges, originalSettings }) {
  const [percentWarning, setPercentWarning] = useState('');

  // Kiểm tra thay đổi chỉ cho các thông số cài đặt hệ thống mưa quà cơ bản
  const hasSystemSettingsChanges = () => {
    if (!originalSettings) return false;
    
    return (
      settings.percentToTreasure !== originalSettings.percentToTreasure ||
      settings.rainDurationSeconds !== originalSettings.rainDurationSeconds ||
      settings.minIntervalBetweenRainsSeconds !== originalSettings.minIntervalBetweenRainsSeconds ||
      settings.jackpotSyncIntervalSeconds !== originalSettings.jackpotSyncIntervalSeconds
    );
  };

  // Validate tỷ lệ trích hũ và hiển thị cảnh báo
  const validatePercentToTreasure = (value) => {
    if (value === '' || value === null || value === undefined) {
      setPercentWarning('');
      return true;
    }

    const numValue = parseFloat(value);
    
    // Kiểm tra không vượt quá 100%
    if (numValue > 100) {
      setPercentWarning('⚠️ Tỷ lệ trích hũ không được vượt quá 100%');
      return false;
    }

    // Cảnh báo ở các mức cao
    if (numValue >= 50) {
      setPercentWarning('🚨 CẢNH BÁO: Tỷ lệ trích hũ rất cao (≥50%). Điều này có thể ảnh hưởng nghiêm trọng đến hũ!');
    } else if (numValue >= 10) {
      setPercentWarning('⚠️ CẢNH BÁO: Tỷ lệ trích hũ cao (≥10%). Hãy cân nhắc kỹ trước khi áp dụng!');
    } else if (numValue >= 5) {
      setPercentWarning('💡 Lưu ý: Tỷ lệ trích hũ khá cao (≥5%). Đảm bảo hũ có đủ dự trữ!');
    } else {
      setPercentWarning('');
    }

    return true;
  };

  const handleSettingChange = (field, value) => {
    onSettingsChange(field, value);
    
    // Validate riêng cho percentToTreasure
    if (field === 'percentToTreasure') {
      validatePercentToTreasure(value);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom style={{ color: '#1976d2', marginBottom: '20px' }}>
          🌧️ Cài đặt hệ thống mưa quà
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tỷ lệ trích hũ (%)"
              type="number"
              value={settings.percentToTreasure}
              onChange={(e) => {
                if (e && e.target) {
                  const value = e.target.value === '' ? '' : parseFloat(e.target.value);
                  handleSettingChange('percentToTreasure', value);
                }
              }}
              fullWidth
              variant="outlined"
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              error={!!percentWarning && percentWarning.includes('không được vượt quá')}
              helperText={
                percentWarning ? 
                  percentWarning : 
                  "Tỷ lệ phần trăm trích từ hũ để làm mưa quà"
              }
              FormHelperTextProps={{
                style: {
                  color: percentWarning.includes('🚨') ? '#d32f2f' : 
                         percentWarning.includes('⚠️') ? '#f57c00' : 
                         percentWarning.includes('💡') ? '#1976d2' : 
                         '#666'
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Thời gian mưa quà (giây)"
              type="number"
              value={settings.rainDurationSeconds}
              onChange={(e) => {
                if (e && e.target) {
                  const value = e.target.value === '' ? '' : parseInt(e.target.value);
                  handleSettingChange('rainDurationSeconds', value);
                }
              }}
              fullWidth
              variant="outlined"
              inputProps={{ min: 1 }}
              helperText="Khoảng thời gian mưa quà diễn ra"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Thời gian giữa 2 lần mưa (giây)"
              type="number"
              value={settings.minIntervalBetweenRainsSeconds}
              onChange={(e) => {
                if (e && e.target) {
                  const value = e.target.value === '' ? '' : parseInt(e.target.value);
                  handleSettingChange('minIntervalBetweenRainsSeconds', value);
                }
              }}
              fullWidth
              variant="outlined"
              inputProps={{ min: 0 }}
              helperText="Thời gian tối thiểu giữa 2 lần mưa quà liên tiếp"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Thời gian đồng bộ hũ (giây)"
              type="number"
              value={settings.jackpotSyncIntervalSeconds}
              onChange={(e) => {
                if (e && e.target) {
                  const value = e.target.value === '' ? '' : parseInt(e.target.value);
                  handleSettingChange('jackpotSyncIntervalSeconds', value);
                }
              }}
              fullWidth
              variant="outlined"
              inputProps={{ min: 0 }}
              helperText="Khoảng thời gian tối thiểu giữa hai lần đồng bộ quỹ lên Firebase; 0 là đồng bộ mỗi giao dịch"
            />
          </Grid>
        </Grid>
        
        <Box style={{ marginTop: '20px', textAlign: 'right' }}>
          <Box style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            {/* Nút Hủy - chỉ hiển thị khi có thay đổi cài đặt hệ thống */}
            {hasSystemSettingsChanges() && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={onCancelChanges}
                disabled={loading.save}
                style={{
                  backgroundColor: '#f44336',
                  '&:hover': {
                    backgroundColor: '#d32f2f'
                  }
                }}
              >
                Hủy
              </Button>
            )}
            
            {/* Nút Lưu - chỉ phản ứng với thay đổi cài đặt hệ thống */}
            <Button
              variant={hasSystemSettingsChanges() ? "contained" : "outlined"}
              color={hasSystemSettingsChanges() ? "secondary" : "primary"}
              startIcon={loading.save ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={async () => {
                try {
                  await onSave();
                } catch (error) {
                  console.error('Lỗi khi lưu cài đặt hệ thống:', error);
                  // Lỗi đã được xử lý trong onSave, không cần làm gì thêm
                }
              }}
              disabled={loading.save}
              style={hasSystemSettingsChanges() ? {
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              } : {
                borderColor: '#e0e0e0',
                color: '#666',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#bdbdbd'
                }
              }}
            >
              {loading.save ? 'Đang lưu...' : 
                hasSystemSettingsChanges() ? 'Lưu cài đặt hệ thống' : 
                'Cập nhật cài đặt hệ thống'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default RainSystemSettings;
